import { useState, useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { PopulationAndType } from '../../utils/dataTypes';
import DiagnosisList from './DiagnosisList';
import { components } from '../../api/types';

type FilterParams = components["schemas"]["FilterParams"];
type Quantiles = components["schemas"]["Quantiles"] | null;

type Props = {
  selectedId: string | null;
  loading: boolean;

  /* Client data */
  age: number | null;
  sex: string;
  race: string | null;
  ethnicity: string | null;

  /* Diagnosis data */
  diagnosisNames: string[];
  diagnosisLabels: (string | null)[];
  oddsRatios: (number | null)[];
  actualDiagnosisMask: boolean[];
  agreedDiagnosisMask: boolean[];
  disagreedDiagnosisMask: boolean[];
  recommendedDiagnosisMask: boolean[];

  /* Measure data */
  measureNames: string[];
  actualScores: (number | null)[];
  actualPercentileRanks: (number | null)[];
  correlationPercents: number[][];
  takenMeasureMask: boolean[];
  quantiles: Quantiles[];

  /* Filter data */
  setFilter: React.Dispatch<React.SetStateAction<FilterParams>>,
};

/**
 * Predicted diagnoses page component
 * 
 * @param selectedId: string - selected client ID
 * @param loading: boolean - loading state
 * 
 * @returns Predicted diagnoses page element
 */
export const PredictedDiagnosesPage = ({
  selectedId,
  loading,

  /* Client data */
  age,
  sex,
  race,
  ethnicity,

  /* Diagnosis data */
  diagnosisNames,
  diagnosisLabels,
  oddsRatios,
  actualDiagnosisMask,
  agreedDiagnosisMask,
  disagreedDiagnosisMask,
  recommendedDiagnosisMask,

  /* Measure data */
  measureNames,
  actualScores,
  actualPercentileRanks,
  correlationPercents,
  takenMeasureMask,
  quantiles,

  /* Filter data */
  setFilter,
}: Props): JSX.Element => {
  const [populationAndType, setPopulationAndType] = useState<PopulationAndType>({population: "", populationType: ""});

  /* Reset the comparison population when the patient changes */
  useEffect(() => {
    setPopulationAndType({population: "", populationType: ""});
  }, [selectedId]);

  /* Update selected filter */
  useEffect(() => {
    const key = populationAndType.populationType;
    const mapping = {
      "": () => setFilter({}),
      Age: () => setFilter({equal: {Age: age ?? 0}}),
      Sex: () => setFilter({equal: {Sex: sex}}),
      // No race/ethnicity recorded: nothing to filter on, so fall back to the general population
      Race: () => setFilter(race ? {equal: {Race: race}} : {}),
      Ethnicity: () => setFilter(ethnicity ? {equal: {Ethnicity: ethnicity}} : {}),
    };
    mapping[key as keyof typeof mapping]();
  }, [populationAndType, setFilter, age, sex, race, ethnicity]);

  /* Text constants */
  const metConsensus = 'found evidence';
  const notMeetConsensus = 'did not find evidence';
  const suggestInvestigation = 'suggests investigating';
  const existingDiagnosesText = ' for the following existing diagnoses:';
  const newDiagnosesText = ' the following diagnoses:';

  return (
    <>
      <h1 className="text">Predicted Diagnoses</h1>
      <div className="allDiagnoses">
        <p className="text content">InsightCare screened for the following diagnoses and found:</p>
        <div className="colorKey text content">
          <span className="yellowText">Yellow</span> = InsightCare found evidence for diagnoses that a clinician has not yet explored.<br/>
          <span className="orangeText">Orange</span> = InsightCare did not find evidence supporting the clinician's diagnosis.<br/>
          <span className="greenText">Green</span> = InsightCare found evidence supporting the clinician's diagnosis.<br/>
        </div>
        { loading
          ?
          <p className="text content">Loading...</p>
          :
          <>
            {/* Actual diagnosis true, prediction false */}
            { disagreedDiagnosisMask.some(v => v) &&
              <DiagnosisList
                /* Diagnosis data */
                diagnosisNames={diagnosisNames.filter((_, i: number) => disagreedDiagnosisMask[i])}
                diagnosisLabels={diagnosisLabels.filter((_, i: number) => disagreedDiagnosisMask[i])}
                oddsRatios={oddsRatios.filter((_, i: number) => disagreedDiagnosisMask[i])}
              
                /* Measure data */
                measureNames={measureNames}
                actualScores={actualScores}
                actualPercentileRanks={actualPercentileRanks}
                correlationPercents={correlationPercents.filter((_, i: number) => disagreedDiagnosisMask[i])}
                takenMeasureMask={takenMeasureMask}
                quantiles={quantiles}
                
                /* Other data */
                colorClass={"orangeText"}
                consensusText={notMeetConsensus}
                concludingText={existingDiagnosesText}
                population={populationAndType}
              />
            }

            {/* Actual diagnosis false, prediction true */}
            { recommendedDiagnosisMask.some(v => v) &&
              <DiagnosisList
                /* Diagnosis data */
                diagnosisNames={diagnosisNames.filter((_, i) => recommendedDiagnosisMask[i])}
                diagnosisLabels={diagnosisLabels.filter((_, i) => recommendedDiagnosisMask[i])}
                oddsRatios={oddsRatios.filter((_, i) => recommendedDiagnosisMask[i])}
              
                /* Measure data */
                measureNames={measureNames}
                actualScores={actualScores}
                actualPercentileRanks={actualPercentileRanks}
                correlationPercents={correlationPercents.filter((_, i: number) => recommendedDiagnosisMask[i])}
                takenMeasureMask={takenMeasureMask}
                quantiles={quantiles}
                
                /* Other data */
                colorClass={"yellowText"}
                consensusText={suggestInvestigation}
                concludingText={newDiagnosesText}
                population={populationAndType}
              />
            }

            {/* Actual diagnosis true, prediction true */}
            { agreedDiagnosisMask.some(v => v) &&
              <DiagnosisList
                /* Diagnosis data */
                diagnosisNames={diagnosisNames.filter((_, i) => agreedDiagnosisMask[i])}
                diagnosisLabels={diagnosisLabels.filter((_, i) => agreedDiagnosisMask[i])}
                oddsRatios={oddsRatios.filter((_, i) => agreedDiagnosisMask[i])}
              
                /* Measure data */
                measureNames={measureNames}
                actualScores={actualScores}
                actualPercentileRanks={actualPercentileRanks}
                correlationPercents={correlationPercents.filter((_, i: number) => agreedDiagnosisMask[i])}
                takenMeasureMask={takenMeasureMask}
                quantiles={quantiles}
                
                /* Other data */
                colorClass={"greenText"}
                consensusText={metConsensus}
                concludingText={existingDiagnosesText}
                population={populationAndType}
              />
            }
          
            <Box sx={{ display: 'block', height: '80px' }} />
          </>
        }
      </div>
    </>
  );
};

export default PredictedDiagnosesPage;
